import React,{useState,useRef} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {exportToCSV,parseCSV} from '../utils/csvUtils';

const {FiDownload,FiUpload,FiFile,FiAlertCircle,FiCheck,FiX}=FiIcons;

const CSVImportExport=({invoices,onImport,onClose})=> {
  const [importResults,setImportResults]=useState(null);
  const [isProcessing,setIsProcessing]=useState(false);
  const fileInputRef=useRef(null);

  const handleExport=()=> {
    if (invoices.length===0) {
      alert('Ni računov za izvoz');
      return;
    }
    exportToCSV(invoices,'racuni');
  };

  const handleFileSelect=async (event)=> {
    const file=event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Prosimo, izberite CSV datoteko');
      return;
    }

    setIsProcessing(true);
    setImportResults(null);

    try {
      const content=await readFileAsText(file);
      const importedInvoices=parseCSV(content);
      setImportResults({
        success: true,
        count: importedInvoices.length,
        invoices: importedInvoices
      });
    } catch (error) {
      setImportResults({
        success: false,
        error: error.message
      });
    } finally {
      setIsProcessing(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value='';
      }
    }
  };

  const readFileAsText=(file)=> {
    return new Promise((resolve,reject)=> {
      const reader=new FileReader();
      reader.onload=(e)=> resolve(e.target.result);
      reader.onerror=(e)=> reject(new Error('Napaka pri branju datoteke'));
      reader.readAsText(file,'UTF-8');
    });
  };

  const handleConfirmImport=()=> {
    if (importResults && importResults.success && importResults.invoices) {
      onImport(importResults.invoices);
      setImportResults(null);
      onClose();
    }
  };

  const handleCancelImport=()=> {
    setImportResults(null);
  };

  const downloadSampleCSV=()=> {
    const sampleData=[
      {
        stranka: 'Primer d.o.o.',
        amount: 150,
        totalAmount: 183,
        interval: 'monthly',
        nextDue: new Date().toISOString(),
        description: 'Mesečna storitev',
        notes: 'Interne opombe',
        lineItems: [
          {service: 'Dodatna storitev 1',price: '25'},
          {service: 'Dodatna storitev 2',price: '8'}
        ]
      }
    ];
    exportToCSV(sampleData,'primer_racuni');
  };

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{scale: 0.9,opacity: 0}}
        animate={{scale: 1,opacity: 1}}
        exit={{scale: 0.9,opacity: 0}}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e)=> e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#4d504a]">Uvoz/Izvoz računov</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-[#6e725c] transition-colors"
            >
              <SafeIcon icon={FiX} className="text-2xl" />
            </button>
          </div>

          {!importResults ? (
            <div className="space-y-8">
              {/* Export Section */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-[#4d504a] mb-4 flex items-center">
                  <SafeIcon icon={FiDownload} className="mr-2" />
                  Izvozi račune
                </h3>
                <p className="text-[#6e725c] mb-4">
                  Izvozite vse račune v CSV datoteko za varnostno kopijo ali prenos podatkov.
                </p>
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg mb-4">
                  <div>
                    <p className="font-medium text-[#4d504a]">Število računov: {invoices.length}</p>
                    <p className="text-sm text-[#6e725c]">Vključene bodo vse postavke in opombe</p>
                  </div>
                  <motion.button
                    onClick={handleExport}
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                    disabled={invoices.length===0}
                    className="bg-[#80b238] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#6d9530] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <SafeIcon icon={FiDownload} className="mr-2" />
                    Izvozi CSV
                  </motion.button>
                </div>
              </div>

              {/* Import Section */}
              <div>
                <h3 className="text-lg font-semibold text-[#4d504a] mb-4 flex items-center">
                  <SafeIcon icon={FiUpload} className="mr-2" />
                  Uvozi račune
                </h3>
                <p className="text-[#6e725c] mb-4">
                  Uvozite račune iz CSV datoteke. Datoteka mora imeti ustrezno strukturo.
                </p>

                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-[#4d504a] mb-2 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-2 text-blue-500" />
                      Pomembno
                    </h4>
                    <ul className="text-sm text-[#6e725c] space-y-1">
                      <li>• Decimalni separator mora biti vejica (,)</li>
                      <li>• Datumi v formatu DD.MM.YYYY</li>
                      <li>• Kodiranje datoteke: UTF-8</li>
                      <li>• Dodatne storitve v formatu: "naziv: cena€;naziv2: cena€"</li>
                    </ul>
                  </div>

                  <div className="flex items-center space-x-4">
                    <motion.button
                      onClick={downloadSampleCSV}
                      whileHover={{scale: 1.05}}
                      whileTap={{scale: 0.95}}
                      className="bg-[#3297c7] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#2a7ca8] transition-colors flex items-center"
                    >
                      <SafeIcon icon={FiFile} className="mr-2" />
                      Prenesi vzorec
                    </motion.button>

                    <div className="flex-1">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="csv-file-input"
                      />
                      <motion.label
                        htmlFor="csv-file-input"
                        whileHover={{scale: 1.05}}
                        whileTap={{scale: 0.95}}
                        className="bg-[#6e725c] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#5d6250] transition-colors cursor-pointer flex items-center justify-center"
                      >
                        <SafeIcon icon={FiUpload} className="mr-2" />
                        {isProcessing ? 'Obdelujem...' : 'Izberi CSV datoteko'}
                      </motion.label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Import Results */
            <div className="space-y-6">
              {importResults.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <SafeIcon icon={FiCheck} className="text-green-500 text-2xl mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">Uvoz uspešen!</h3>
                      <p className="text-green-600">
                        Uspešno uvoženih {importResults.count} račun{importResults.count===1 ? '' : importResults.count <=4 ? 'i' : 'ov'}
                      </p>
                    </div>
                  </div>

                  {/* Preview of imported invoices */}
                  <div className="bg-white rounded-lg p-4 mb-4 max-h-60 overflow-y-auto">
                    <h4 className="font-medium text-[#4d504a] mb-3">Predogled uvoženih računov:</h4>
                    <div className="space-y-2">
                      {importResults.invoices.slice(0,5).map((invoice,index)=> (
                        <div key={index} className="flex justify-between items-center text-sm border-b pb-2">
                          <span className="font-medium">{invoice.stranka}</span>
                          <span className="text-[#80b238]">{(invoice.totalAmount || 0).toFixed(2).replace('.',',')} €</span>
                        </div>
                      ))}
                      {importResults.invoices.length > 5 && (
                        <div className="text-center text-gray-500 text-sm pt-2">
                          ... in še {importResults.invoices.length - 5} račun{importResults.invoices.length - 5===1 ? '' : 'ov'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <motion.button
                      onClick={handleConfirmImport}
                      whileHover={{scale: 1.05}}
                      whileTap={{scale: 0.95}}
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                    >
                      Potrdi uvoz
                    </motion.button>
                    <motion.button
                      onClick={handleCancelImport}
                      whileHover={{scale: 1.05}}
                      whileTap={{scale: 0.95}}
                      className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                    >
                      Prekliči
                    </motion.button>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 text-2xl mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-red-800">Napaka pri uvozu</h3>
                      <p className="text-red-600">{importResults.error}</p>
                    </div>
                  </div>
                  <motion.button
                    onClick={handleCancelImport}
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
                  >
                    Zapri
                  </motion.button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CSVImportExport;